/**
 * Pure copy logic for the Pro-Feature upsell modal.
 *
 * Three feature walls currently send a non-eligible user STRAIGHT to the Paddle
 * checkout with no interstitial: the stem splitter, master-quality rendering,
 * and premium advanced settings. This modal is the interstitial — it shows what
 * the feature unlocks before the checkout opens. The origin → copy mapping lives
 * here so it can be unit-tested without React.
 *
 * NOT a plan gate. The call sites already gate on `!canUseFeature(...)`, so the
 * modal only ever opens for a user who genuinely can't use the feature (free OR
 * a lower-tier payer who lacks it) — for whom "upgrade to unlock" is always the
 * correct message. There is deliberately no free-only check here (unlike the
 * value-moment upsell, which IS free-only).
 */

/** The exact origin strings passed by the three interstitial-less pro walls. */
export type ProUpsellOrigin =
  | "stem-splitter"
  | "master-quality"
  | "premium-advanced-settings";

export interface ProUpsellCopy {
  eyebrow: string;
  headline: string;
  sub: string;
}

/** Feature-specific copy keyed by the checkout `origin`. */
const COPY: Readonly<Record<string, ProUpsellCopy>> = {
  "stem-splitter": {
    eyebrow: "Pro tool",
    headline: "split any track into stems",
    sub: "isolate vocals, drums, bass, and instruments from any song — clean, studio-grade separation.",
  },
  "master-quality": {
    eyebrow: "HD quality",
    headline: "render in master quality",
    sub: "the highest-fidelity model for crisp, release-ready vocals — no more compressed output.",
  },
  "premium-advanced-settings": {
    eyebrow: "Pro controls",
    headline: "unlock the pro controls",
    sub: "fine-tune every generation with the advanced settings the pros use.",
  },
};

/** Safe default for any origin not in the map — never throws, always usable. */
const FALLBACK: ProUpsellCopy = {
  eyebrow: "Musicfy Pro",
  headline: "unlock the pro features",
  sub: "upgrade to get every Musicfy tool, in full quality.",
};

/** Map a checkout `origin` to its feature copy; unknown/absent → FALLBACK. */
export function proUpsellCopy(
  origin: string | undefined | null
): ProUpsellCopy {
  if (!origin) return FALLBACK;
  return COPY[origin] ?? FALLBACK;
}
